import React, { useState, useContext, useEffect, useCallback } from "react";
import { DataContext } from "../contexts/DataContext";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExcel,
  faSpinner,
  faTimes,
  faInfoCircle,
  faDownload,
  faObjectGroup,
} from "@fortawesome/free-solid-svg-icons";

/**
 * Component for generating reports from reference numbers
 * Example usage:
 * <ReferencesReport />
 */
const CartaPorteReport = () => {
  const [referencesInput, setReferencesInput] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [consolidateReferences, setConsolidateReferences] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState("HAM PL5");
  const [isLoadingDestinations, setIsLoadingDestinations] = useState(false);

  const [jobId, setJobId] = useState(null);
  const [jobProgress, setJobProgress] = useState(0);
  const [currentReference, setCurrentReference] = useState("");
  const [isPolling, setIsPolling] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Get context data and functions
  const { fetchTrafficDataByReferences, setShouldFetchData } =
    useContext(DataContext);

  const pollJobStatus = useCallback(
    async (jobId) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/api/traffic/report-status/${jobId}`
        );

        const { state, progress, data, result } = response.data;

        // Ensure progress is a number
        const progressValue =
          typeof progress === "object" && progress.progress
            ? progress.progress
            : progress || 0;

        setJobProgress(progressValue);

        // Extract all available data
        if (data) {
          if (data.currentReference) {
            setCurrentReference(data.currentReference);
          }
          if (data.processedCount !== undefined) {
            setProcessedCount(data.processedCount);
          }
          if (data.totalCount !== undefined) {
            setTotalCount(data.totalCount);
          }
          // If we have total references from job data
          if (data.totalReferences) {
            setTotalCount(data.totalReferences);
          }
        }

        if (state === "completed") {
          setIsPolling(false);
          setIsExporting(false);

          // Download the file
          const downloadResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_API_URL}/api/traffic/download-report/${jobId}`,
            {
              responseType: "blob",
              headers: {
                Accept:
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              },
            }
          );

          // Create blob and download
          const blob = new Blob([downloadResponse.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = consolidateReferences
            ? "Consolidated_References_Report.xlsx"
            : "Enhanced_References_Report.xlsx";
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);

          setMessage("Excel report downloaded successfully.");
          setJobId(null);
          setJobProgress(0);
          setCurrentReference("");
          setProcessedCount(0);
          setTotalCount(0);
        } else if (state === "failed") {
          setIsPolling(false);
          setIsExporting(false);
          setError(
            `Report generation failed: ${
              result?.failedReason || "Unknown error"
            }`
          );
          setJobId(null);
          setJobProgress(0);
          setCurrentReference("");
          setProcessedCount(0);
          setTotalCount(0);
        } else if (state === "stuck") {
          // Handle stuck jobs
          setCurrentReference("Job appears to be stuck, please wait...");
        } else {
          // Continue polling
          setTimeout(() => pollJobStatus(jobId), 1000);
        }
      } catch (error) {
        console.error("Error polling job status:", error);
        setIsPolling(false);
        setIsExporting(false);
        setError("Error checking report status");
        setJobId(null);
        setJobProgress(0);
        setCurrentReference("");
        setProcessedCount(0);
        setTotalCount(0);
      }
    },
    [consolidateReferences]
  );

  useEffect(() => {
    const fetchDestinations = async () => {
      setIsLoadingDestinations(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/api/cartaPorteDestinations`
        );
        setDestinations(response.data);
        setIsLoadingDestinations(false);
      } catch (error) {
        console.error("Error fetching destinations:", error);
        setIsLoadingDestinations(false);
      }
    };

    fetchDestinations();
  }, []);

  useEffect(() => {
    // Enable fetching for the component
    setShouldFetchData((prev) => ({
      ...prev,
      fetchTrafficDataByReferences: true,
    }));

    return () => {
      // Disable fetching when component unmounts
      setShouldFetchData((prev) => ({
        ...prev,
        fetchTrafficDataByReferences: false,
      }));
    };
  }, [setShouldFetchData]);

  /**
   * Parses the input text area to extract reference numbers
   */
  const parseReferences = () => {
    // Split by commas, new lines, or spaces
    const rawEntries = referencesInput
      .split(/[\s,\n]+/)
      .map((entry) => entry.trim())
      .filter(Boolean);

    return rawEntries;
  };

  const fetchEnhancedData = async (references) => {
    try {
      // Try to use the backend endpoint for blending
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/traffic/enhanced-traffic-data`,
        { references }
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error using enhanced endpoint, falling back to manual blending:",
        error
      );

      // Fallback to manual blending if the endpoint fails
      const enhancedResults = [];

      for (const reference of references) {
        try {
          // Standard data request
          const standardResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_API_URL}/api/traffic`,
            { params: { reference } }
          );

          // Detailed item data request
          const detailedResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_API_URL}/api/traffic`,
            { params: { reference, responseType: "I" } }
          );

          // Extract data from the nested structure
          const standardData = standardResponse.data["0"] || {};
          const detailedData = detailedResponse.data["0"] || {};

          // Properly blend the data
          const enhancedData = {
            header: standardData.header || {},
            // Merge the invoice data with classification details
            invoice_data: detailedData.invoice_data || [],
            // Keep any other relevant data from both responses
            traffic_data: standardData.traffic_data || {},
            summary: standardData.summary || {},
          };

          enhancedResults.push(enhancedData);
        } catch (refError) {
          console.error(`Error processing reference ${reference}:`, refError);
          enhancedResults.push({
            reference,
            error: refError.message,
            hasError: true,
          });
        }
      }

      return enhancedResults;
    }
  };

  /**
   * Handles the generation of the report
   */
  const handleGenerateReport = async () => {
    setError("");
    setMessage("");
    setResults([]);
    setPreviewData(null);

    const references = parseReferences();
    if (references.length === 0) {
      setError("Please enter at least one valid reference number.");
      return;
    }

    setIsGenerating(true);
    try {
      // Use the enhanced endpoint to get blended data
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/traffic/enhanced-traffic-data`,
        { references }
      );

      const enhancedResults = response.data;

      if (enhancedResults.length > 0) {
        setResults(enhancedResults);

        // Count successful references (those without errors)
        const successfulRefs = enhancedResults.filter(
          (r) => !r.hasError && r.status !== "error"
        ).length;

        setMessage(
          `Enhanced report generated successfully for ${successfulRefs} of ${enhancedResults.length} reference(s).`
        );

        // Set a preview of the first successful result
        const firstValidResult = enhancedResults.find(
          (r) => !r.hasError && r.status !== "error"
        );
        if (firstValidResult) {
          setPreviewData(firstValidResult);
        } else {
          setError(
            "Could not retrieve valid data for any of the provided references."
          );
        }
      } else {
        setError(
          "No data found for the specified references. Please check and try again."
        );
      }
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message ||
          "Error generating enhanced report. Please check the references and try again."
      );
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Exports the data to Excel by requesting the backend to generate it
   */
  const exportToExcel = async () => {
    const references = parseReferences();
    if (references.length === 0) {
      setError("Please enter at least one valid reference number.");
      return;
    }

    if (consolidateReferences && references.length <= 1) {
      setError("Consolidation requires at least two valid references.");
      return;
    }

    setIsExporting(true);
    setError("");
    setMessage("");
    setJobProgress(0);
    setCurrentReference("Initializing...");
    setProcessedCount(0);
    setTotalCount(references.length); // Set total count upfront

    try {
      const endpoint = consolidateReferences
        ? `${process.env.REACT_APP_BACKEND_API_URL}/api/traffic/generateConsolidatedCartaPorteExcelReport`
        : `${process.env.REACT_APP_BACKEND_API_URL}/api/traffic/generateCartaPorteExcelReport`;

      // Start the job
      const response = await axios.post(endpoint, {
        references,
        destinationId: selectedDestination,
      });

      const { jobId } = response.data;
      setJobId(jobId);
      setIsPolling(true);

      // Start polling for status
      pollJobStatus(jobId);
    } catch (err) {
      console.error("Error starting Excel export:", err);
      setError(
        err.response?.data?.message ||
          `Failed to start export. Please try again.`
      );
      setIsExporting(false);
      setProcessedCount(0);
      setTotalCount(0);
    }
  };

  /**
   * Toggles the JSON preview
   */
  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h2 className="text-2xl font-semibold text-gray-800 mb-4">
        Traffic References Report Generator
      </h2>

      {/* References Input */}
      <div className="mb-4">
        <label htmlFor="referencesInput" className="block mb-2 font-medium">
          Enter Reference Number(s):
        </label>
        <textarea
          id="referencesInput"
          rows={5}
          className="w-full p-2 border border-gray-300 rounded"
          placeholder="e.g., 4TMM01718, 4TMM02183, 4TMM02441"
          value={referencesInput}
          onChange={(e) => setReferencesInput(e.target.value)}
        ></textarea>
        <p className="text-sm text-gray-500 mt-1">
          Enter multiple references separated by commas, spaces, or new lines.
        </p>
      </div>

      {/* Destination Selection */}
      <div className="mb-4">
        <label htmlFor="destinationSelect" className="block mb-2 font-medium">
          Select Destination Location:
        </label>
        <div className="relative">
          {isLoadingDestinations ? (
            <div className="flex items-center">
              <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
              Loading destinations...
            </div>
          ) : (
            <select
              id="destinationSelect"
              className="w-full p-2 border border-gray-300 rounded"
              value={selectedDestination}
              onChange={(e) => setSelectedDestination(e.target.value)}
            >
              {destinations.map((dest) => (
                <option key={dest.locationId} value={dest.locationId}>
                  {dest.locationId} - {dest.name} ({dest.estadoDesc})
                </option>
              ))}
            </select>
          )}
        </div>
        <p className="text-sm text-gray-500 mt-1">
          This location will be used in the "DESTINO" section of the report.
        </p>
      </div>

      {/* Consolidation Option */}
      <div className="mb-4 flex items-center">
        <input
          type="checkbox"
          id="consolidateReferences"
          className="mr-2 h-4 w-4 text-blue-600"
          checked={consolidateReferences}
          onChange={(e) => setConsolidateReferences(e.target.checked)}
        />
        <label htmlFor="consolidateReferences" className="font-medium">
          Consolidate multiple references into a single report
        </label>
        <div className="ml-2 text-sm text-gray-600">
          <FontAwesomeIcon icon={faObjectGroup} className="mr-1" />
          Combines data from all references using header info from the first
          reference
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-4 flex flex-wrap gap-4">
        {/* Preview Data Button */}
        <button
          className="px-4 py-2 bg-blue-500 text-white font-semibold rounded hover:bg-blue-600 disabled:opacity-50"
          onClick={handleGenerateReport}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
              Generating Preview...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
              Preview Data
            </>
          )}
        </button>

        {/* Export to Excel Button */}
        <button
          className="px-4 py-2 bg-green-600 text-white font-semibold rounded hover:bg-green-700 disabled:opacity-50"
          onClick={exportToExcel}
          disabled={isExporting}
        >
          {isExporting ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
              Generating Excel...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
              Download Excel Report{" "}
              {consolidateReferences ? "(Consolidated)" : ""}
            </>
          )}
        </button>

        {/* Toggle Preview Button - Only shown when preview data is available */}
        {previewData && (
          <button
            className="px-4 py-2 bg-purple-500 text-white font-semibold rounded hover:bg-purple-600"
            onClick={togglePreview}
          >
            <FontAwesomeIcon
              icon={showPreview ? faTimes : faInfoCircle}
              className="mr-2"
            />
            {showPreview ? "Hide JSON Preview" : "Show JSON Preview"}
          </button>
        )}
      </div>

      {/* Enhanced Progress Display */}
      {isExporting && (
        <div className="w-full mt-4">
          <div className="p-4 bg-blue-50 rounded-lg shadow-sm">
            <div className="space-y-3">
              {/* Progress Header */}
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-semibold text-blue-900">
                  {consolidateReferences
                    ? "Consolidating References"
                    : "Generating Report"}
                </h4>
                <span className="text-lg font-bold text-blue-700">
                  {jobProgress}%
                </span>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${jobProgress}%` }}
                >
                  <div className="h-full bg-white bg-opacity-20 animate-pulse"></div>
                </div>
              </div>

              {/* Status Details */}
              <div className="space-y-2 text-sm">
                {currentReference && (
                  <div className="flex items-start space-x-2">
                    <FontAwesomeIcon
                      icon={faSpinner}
                      className="mt-0.5 animate-spin text-blue-600"
                    />
                    <div className="flex-1">
                      <p className="text-gray-700 font-medium">
                        Current Status:
                      </p>
                      <p className="text-gray-600">{currentReference}</p>
                    </div>
                  </div>
                )}

                {totalCount > 0 && (
                  <div className="flex items-center justify-between bg-white bg-opacity-50 rounded px-3 py-2">
                    <span className="text-gray-600">References:</span>
                    <span className="font-medium text-gray-800">
                      {processedCount} / {totalCount}
                    </span>
                  </div>
                )}

                {/* Progress stages indicator */}
                {jobProgress > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center space-x-1 text-xs">
                      <div
                        className={`flex-1 h-1 rounded ${
                          jobProgress >= 20 ? "bg-blue-600" : "bg-gray-300"
                        }`}
                      ></div>
                      <div
                        className={`flex-1 h-1 rounded ${
                          jobProgress >= 40 ? "bg-blue-600" : "bg-gray-300"
                        }`}
                      ></div>
                      <div
                        className={`flex-1 h-1 rounded ${
                          jobProgress >= 60 ? "bg-blue-600" : "bg-gray-300"
                        }`}
                      ></div>
                      <div
                        className={`flex-1 h-1 rounded ${
                          jobProgress >= 80 ? "bg-blue-600" : "bg-gray-300"
                        }`}
                      ></div>
                      <div
                        className={`flex-1 h-1 rounded ${
                          jobProgress >= 100 ? "bg-blue-600" : "bg-gray-300"
                        }`}
                      ></div>
                    </div>
                    <div className="flex justify-between mt-1 text-xs text-gray-500">
                      <span>Init</span>
                      <span>Data</span>
                      <span>Process</span>
                      <span>Format</span>
                      <span>Done</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Estimated time or additional info */}
              {jobProgress > 0 && jobProgress < 100 && (
                <div className="text-xs text-gray-500 text-center">
                  Processing large reports may take a few moments...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success/Info Message */}
      {message && (
        <div className="mt-4 p-3 text-green-800 bg-green-200 rounded">
          {message}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-3 text-red-800 bg-red-200 rounded">{error}</div>
      )}

      {/* Results Summary */}
      {results.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-3">
            Enhanced Report Summary:
          </h3>
          <div className="bg-gray-100 p-4 rounded">
            <p>
              <strong>Total References:</strong> {results.length}
            </p>
            <ul className="mt-2 list-disc list-inside">
              {results.map((item, index) => (
                <li key={index} className="mt-1">
                  {item.hasError || item.status === "error" ? (
                    <span className="text-red-500">
                      {item.reference || `Reference ${index + 1}`} - Error:{" "}
                      {item.error}
                    </span>
                  ) : (
                    <>
                      {item.header?.reference || `Reference ${index + 1}`} -{" "}
                      {item.header?.customer_name || "Unknown Customer"}
                      {item.invoices && item.invoices.length > 0 && (
                        <span className="ml-2 text-green-600">
                          ({item.invoices.length} invoice(s),
                          {item.invoices.reduce(
                            (total, inv) =>
                              total +
                              (inv.classification_data
                                ? inv.classification_data.length
                                : 0),
                            0
                          )}{" "}
                          classification items)
                        </span>
                      )}
                      {item._warning && (
                        <span className="ml-2 text-amber-600">
                          (Warning: {item._warning})
                        </span>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* JSON Preview */}
      {showPreview && previewData && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-3">JSON Data Preview:</h3>
          <div className="bg-gray-800 text-green-400 p-4 rounded overflow-auto max-h-96">
            <pre>{JSON.stringify(previewData, null, 2)}</pre>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            This is a preview of the raw data. Download the Excel file for a
            formatted report.
            {previewData.invoices && previewData.invoices.length > 0 && (
              <span className="block mt-1">
                This reference includes {previewData.invoices.length} invoice(s)
                with a total of{" "}
                {previewData.invoices.reduce(
                  (total, inv) =>
                    total +
                    (inv.classification_data
                      ? inv.classification_data.length
                      : 0),
                  0
                )}{" "}
                classification items.
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default CartaPorteReport;
